import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { api } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { KeyRound, ArrowLeft, Eye, EyeOff, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const BG = 'https://images.unsplash.com/photo-1766785368863-f2188a8c8b32?auto=format&fit=crop&w=1920&q=60';

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';

  const [password, setPassword]   = useState('');
  const [confirm, setConfirm]     = useState('');
  const [showPw, setShowPw]       = useState(false);
  const [loading, setLoading]     = useState(false);
  const [success, setSuccess]     = useState(false);

  useEffect(() => {
    if (!token) toast.error('Invalid reset link');
  }, [token]);

  const handleSubmit = async e => {
    e.preventDefault();
    if (!password || password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    if (password !== confirm) { toast.error('Passwords do not match'); return; }
    if (!token) { toast.error('Invalid or missing reset token'); return; }
    setLoading(true);
    try {
      await api.post('/auth/reset-password', { token, password });
      setSuccess(true);
      toast.success('Password updated successfully!');
      setTimeout(() => navigate('/'), 2500);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reset failed. The link may have expired.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden">
      <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${BG})` }} />
      <div className="absolute inset-0 bg-black/60" />

      <div className="relative z-10 w-full max-w-md mx-4">
        <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-8 shadow-2xl">

          {!token ? (
            <div className="text-center py-4">
              <AlertCircle className="text-red-400 mx-auto mb-4" size={48} />
              <h2 className="text-xl font-bold text-white mb-2">Invalid Reset Link</h2>
              <p className="text-white/60 text-sm">This link is invalid or has expired. Please request a new one.</p>
            </div>
          ) : success ? (
            <div className="text-center py-4">
              <CheckCircle className="text-green-400 mx-auto mb-4" size={48} />
              <h2 className="text-xl font-bold text-white mb-2">Password Updated!</h2>
              <p className="text-white/60 text-sm">Redirecting you to login...</p>
            </div>
          ) : (
            <>
              <div className="text-center mb-8">
                <div className="w-14 h-14 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <KeyRound className="text-blue-300" size={26} />
                </div>
                <h1 className="text-2xl font-bold text-white">Set New Password</h1>
                <p className="text-white/60 mt-2 text-sm">Choose a strong password (min. 6 characters)</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="relative">
                  <Input
                    type={showPw ? 'text' : 'password'}
                    placeholder="New password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    className="bg-white/10 border-white/20 text-white placeholder:text-white/40 h-11 pr-10"
                    autoFocus
                    data-testid="new-password-input"
                  />
                  <button type="button" onClick={() => setShowPw(v => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white transition-colors">
                    {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                <Input
                  type="password"
                  placeholder="Confirm password"
                  value={confirm}
                  onChange={e => setConfirm(e.target.value)}
                  className="bg-white/10 border-white/20 text-white placeholder:text-white/40 h-11"
                  data-testid="confirm-password-input"
                />
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-11 bg-blue-600 hover:bg-blue-700 text-white font-semibold"
                  data-testid="reset-submit-btn"
                >
                  {loading ? 'Updating...' : 'Update Password'}
                </Button>
              </form>
            </>
          )}

          <div className="mt-6 text-center">
            <Link to="/" className="inline-flex items-center gap-1.5 text-white/50 hover:text-white text-sm transition-colors">
              <ArrowLeft size={14} /> Back to Login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
